import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Search, Plus, Minus, ChevronRight, MessageSquare, Edit2, Trash2 } from 'lucide-react';
import { supabase, updateSessionTitle } from '../lib/supabase';
import type { LearningSession } from '../types';
import anime from 'animejs';

interface ConversationSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  sessions: LearningSession[];
  activeSessionId?: string;
  onSessionSelect: (session: LearningSession) => void;
  onNewSession: () => void;
  onDeleteSession: (sessionId: string) => void;
  onSessionUpdate: (session: LearningSession) => void;
}

function ConversationSidebar({
  isOpen,
  onToggle,
  sessions,
  activeSessionId,
  onSessionSelect,
  onNewSession,
  onDeleteSession,
  onSessionUpdate
}: ConversationSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const editInputRef = useRef<HTMLInputElement>(null);
  const sessionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const bubbleRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const MAX_VISIBLE_BUBBLES = 3;
  const MAX_TOTAL_SESSIONS = 8;

  const filteredSessions = sessions
    .filter(session =>
      session.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      session.question.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .slice(0, MAX_TOTAL_SESSIONS);

  const visibleSessions = filteredSessions.slice(0, MAX_VISIBLE_BUBBLES);
  const remainingCount = Math.max(0, filteredSessions.length - MAX_VISIBLE_BUBBLES);

  useEffect(() => {
    if (containerRef.current && isOpen) {
      // Create floating animation
      anime({
        targets: containerRef.current,
        translateY: [0, -8, 0],
        duration: 6000,
        easing: 'easeInOutSine',
        direction: 'alternate',
        loop: true
      });

      // Create pulsing glow effect
      anime({
        targets: '.sidebar-glow',
        opacity: [0.3, 0.6],
        scale: [1, 1.2],
        duration: 4000,
        easing: 'easeInOutSine',
        direction: 'alternate',
        loop: true
      });

      // Create floating particles
      const createParticle = () => {
        const particle = document.createElement('div');
        particle.className = 'absolute w-1 h-1 rounded-full bg-blue-400/30';
        containerRef.current?.appendChild(particle);
        
        const startX = Math.random() * 100;
        const startY = Math.random() * 100;
        
        anime({
          targets: particle,
          translateX: [
            { value: `${startX}%`, duration: 0 },
            { value: `${startX + (Math.random() * 20 - 10)}%`, duration: 3000 }
          ],
          translateY: [
            { value: `${startY}%`, duration: 0 },
            { value: `${startY - 20}%`, duration: 3000 }
          ],
          opacity: [0, 0.8, 0],
          scale: [0, 1.5, 0],
          duration: 3000,
          easing: 'easeOutSine',
          complete: () => particle.remove()
        });
      };

      // Create particles periodically
      const particleInterval = setInterval(() => {
        createParticle();
      }, 1000);

      return () => clearInterval(particleInterval);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        if (showDeleteConfirm) {
          setShowDeleteConfirm(null);
        }
        if (editingSessionId) {
          setEditingSessionId(null);
        }
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showDeleteConfirm, editingSessionId]);

  useEffect(() => {
    if (editingSessionId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingSessionId]);

  const animateDelete = async (sessionId: string) => {
    const element = sessionRefs.current[sessionId];
    if (!element) return;

    // Create neural spark effect
    const createNeuralSpark = () => {
      const spark = document.createElement('div');
      spark.className = 'absolute w-1 h-1 bg-blue-500 rounded-full';
      spark.style.boxShadow = '0 0 8px 2px rgba(59, 130, 246, 0.4)';
      element.appendChild(spark);
      return spark;
    };

    // Create multiple neural sparks
    const sparks = Array.from({ length: 10 }, createNeuralSpark);

    // Initial spark animation
    await anime({
      targets: sparks,
      translateX: (el, i) => [0, anime.random(-40, 40)],
      translateY: (el, i) => [0, anime.random(-20, 20)],
      scale: [0, 1.2],
      opacity: [1, 0],
      duration: 400,
      delay: anime.stagger(20),
      easing: 'easeOutExpo',
      complete: () => sparks.forEach(spark => spark.remove())
    }).finished;

    // Quick shake animation
    await anime({
      targets: element,
      translateX: [
        { value: -4, duration: 40 },
        { value: 4, duration: 40 },
        { value: -3, duration: 40 },
        { value: 3, duration: 40 },
        { value: 0, duration: 40 }
      ],
      duration: 200,
      easing: 'easeInOutSine'
    }).finished;

    // Create final particles
    const finalParticles = Array.from({ length: 12 }, () => {
      const particle = document.createElement('div');
      particle.className = 'absolute w-1.5 h-1.5 rounded-full';
      particle.style.background = `rgba(${anime.random(59, 130)}, ${anime.random(130, 246)}, ${anime.random(200, 255)}, ${anime.random(0.7, 1)})`;
      element.appendChild(particle);
      particle.style.left = `${anime.random(0, 100)}%`;
      particle.style.top = `${anime.random(0, 100)}%`;
      return particle;
    });

    // Final vanishing animation with particle dispersion
    await anime({
      targets: element,
      translateY: [-10, -60],
      scale: [1, 0.9],
      opacity: [1, 0],
      duration: 400,
      easing: 'easeOutQuart',
      begin: () => {
        // Particle dispersion animation
        anime({
          targets: finalParticles,
          translateX: () => anime.random(-60, 60),
          translateY: () => anime.random(-60, 0),
          scale: [1, 0],
          opacity: [1, 0],
          duration: 500,
          easing: 'easeOutExpo',
          complete: () => {
            finalParticles.forEach(p => p.remove());
            onDeleteSession(sessionId);
          }
        });
      }
    }).finished;
  };

  const handleDeleteClick = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(sessionId);
  };

  const handleDeleteConfirm = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      setShowDeleteConfirm(null);
      await animateDelete(sessionId);
    } catch (err) {
      console.error('Error deleting session:', err);
    }
  };

  const handleDeleteCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteConfirm(null);
  };

  const handleEditClick = (session: LearningSession, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(session.id);
    setEditingTitle(session.subject);
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingTitle(e.target.value);
  };

  const handleTitleSubmit = async (sessionId: string, e?: React.FormEvent) => {
    e?.preventDefault();
    if (!editingTitle.trim()) return;

    try {
      const updatedSession = await updateSessionTitle(sessionId, editingTitle.trim());
      if (updatedSession) {
        onSessionUpdate(updatedSession);
      }
    } catch (error) {
      console.error('Error updating session title:', error);
    } finally {
      setEditingSessionId(null);
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, sessionId: string) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleTitleSubmit(sessionId);
    }
  };

  const handleTitleBlur = (sessionId: string) => {
    handleTitleSubmit(sessionId);
  };

  const animateNeuralSelection = (element: HTMLElement) => {
    // Create neural spark effect
    const sparks = Array.from({ length: 10 }, () => {
      const spark = document.createElement('div');
      spark.className = 'absolute w-1 h-1 rounded-full';
      spark.style.background = 'linear-gradient(to right, rgba(59, 130, 246, 0.8), rgba(147, 51, 234, 0.8))';
      spark.style.boxShadow = '0 0 8px 2px rgba(59, 130, 246, 0.4)';
      element.appendChild(spark);
      return spark;
    });

    anime({
      targets: sparks,
      translateX: () => anime.random(-30, 30),
      translateY: () => anime.random(-30, 30),
      scale: [0, 2],
      opacity: [1, 0],
      duration: 800,
      delay: anime.stagger(50),
      easing: 'easeOutExpo',
      complete: () => sparks.forEach(spark => spark.remove())
    });

    // Ripple effect
    const ripple = document.createElement('div');
    ripple.className = 'absolute inset-0 rounded-xl';
    ripple.style.border = '2px solid rgba(59, 130, 246, 0.5)';
    element.appendChild(ripple);

    anime({
      targets: ripple,
      scale: [1, 1.5],
      opacity: [1, 0],
      duration: 1000,
      easing: 'easeOutExpo',
      complete: () => ripple.remove()
    });
  };

  const handleBubbleClick = (session: LearningSession, element: HTMLElement) => {
    animateNeuralSelection(element);
    onSessionSelect(session);
  };

  const renderCollapsedView = () => (
    <div className="py-4 flex flex-col items-center space-y-4">
      {visibleSessions.map((session) => (
        <button
          key={session.id}
          ref={el => bubbleRefs.current[session.id] = el}
          onClick={(e) => handleBubbleClick(session, e.currentTarget)}
          className={`relative w-10 h-10 rounded-xl transition-all duration-300 transform hover:scale-105 ${
            session.id === activeSessionId
              ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 shadow-lg shadow-blue-500/20'
              : 'bg-slate-800/50 hover:bg-slate-700/50'
          }`}
        >
          <div className={`absolute inset-0 rounded-xl transition-opacity duration-300 ${
            session.id === activeSessionId ? 'opacity-100' : 'opacity-0'
          }`}>
            <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 animate-pulse" />
          </div>
          <MessageSquare className={`w-5 h-5 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
            session.id === activeSessionId ? 'text-blue-400' : 'text-slate-400'
          }`} />
        </button>
      ))}
      
      {remainingCount > 0 && (
        <div className="relative w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 flex items-center justify-center group hover:from-purple-500/20 hover:to-blue-500/20 transition-all duration-300">
          <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-purple-500/10 to-blue-500/10 animate-pulse opacity-50" />
          <span className="text-sm font-medium text-slate-300 group-hover:text-white transition-colors">
            +{remainingCount}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={onToggle}
        />
      )}

      <button
        onClick={onToggle}
        className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-slate-900/90 border border-slate-700/50 text-slate-400 hover:text-slate-200 transition-colors lg:hidden"
      >
        {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      <div
        ref={containerRef}
        className={`fixed top-0 left-0 h-full transform transition-all duration-500 ease-out-expo z-40 ${
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${isCollapsed ? 'w-16' : 'w-80'}`}
      >
        {/* Floating island effect */}
        <div className="absolute inset-0 sidebar-glow rounded-r-2xl bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-indigo-500/10" />
        
        <div className="relative h-full glass-panel rounded-r-2xl border-r border-slate-700/30 backdrop-blur-xl overflow-hidden">
          {/* Neural network effect */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="neural-grid" />
          </div>

          {/* Content */}
          <div className="relative h-full z-10">
            <div className="flex flex-col h-full">
              <div className="p-4 border-b border-slate-700/30">
                <div className="flex items-center justify-between mb-4">
                  {!isCollapsed && (
                    <h2 className="text-lg font-display font-bold bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent">
                      Conversations
                    </h2>
                  )}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setIsCollapsed(!isCollapsed)}
                      className="p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-slate-200 transition-colors"
                    >
                      {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <Minus className="w-5 h-5" />}
                    </button>
                    {!isCollapsed && (
                      <button
                        onClick={onNewSession}
                        className="p-2 rounded-lg bg-slate-800/50 text-slate-400 hover:text-slate-200 transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </div>
                {!isCollapsed && (
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      type="text"
                      placeholder="Search conversations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full bg-slate-800/30 border border-slate-700/30 rounded-lg pl-10 pr-4 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
                    />
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto">
                {isCollapsed ? (
                  renderCollapsedView()
                ) : (
                  filteredSessions.length === 0 ? (
                    <div className="p-4 text-center text-slate-400">
                      <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No conversations found</p>
                    </div>
                  ) : (
                    <div className="space-y-1 p-2">
                      {filteredSessions.map((session) => (
                        <div
                          key={session.id}
                          ref={el => sessionRefs.current[session.id] = el}
                          onClick={() => onSessionSelect(session)}
                          className={`group relative p-3 rounded-lg cursor-pointer transition-all duration-300 overflow-hidden ${
                            session.id === activeSessionId
                              ? 'bg-blue-500/10 hover:bg-blue-500/20'
                              : 'hover:bg-slate-800/30'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              {editingSessionId === session.id ? (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <input
                                    ref={editInputRef}
                                    type="text"
                                    value={editingTitle}
                                    onChange={handleTitleChange}
                                    onKeyDown={(e) => handleTitleKeyDown(e, session.id)}
                                    onBlur={() => handleTitleBlur(session.id)}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg px-3 py-1 text-sm text-slate-100 focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 transition-all"
                                  />
                                </div>
                              ) : (
                                <>
                                  <div className="flex items-center">
                                    <h3 className={`font-medium truncate ${
                                      session.id === activeSessionId
                                        ? 'bg-gradient-to-r from-blue-400 via-indigo-400 to-purple-400 bg-clip-text text-transparent'
                                        : 'text-slate-200'
                                    }`}>
                                      {session.subject || 'Untitled Conversation'}
                                    </h3>
                                    <button
                                      onClick={(e) => handleEditClick(session, e)}
                                      className="ml-2 p-1 opacity-0 group-hover:opacity-100 rounded-md text-slate-400 hover:text-blue-400 transition-all"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                  <p className="text-sm text-slate-400 truncate mt-0.5">
                                    {session.question}
                                  </p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {new Date(session.created_at).toLocaleDateString()}
                                  </p>
                                </>
                              )}
                            </div>
                            
                            {showDeleteConfirm === session.id ? (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => handleDeleteConfirm(session.id, e)}
                                  className="p-1 rounded-md bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handleDeleteCancel}
                                  className="p-1 rounded-md bg-slate-800/50 text-slate-400 hover:text-slate-200 transition-colors"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={(e) => handleDeleteClick(session.id, e)}
                                className="p-1 rounded-md opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-400 transition-all"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          
                          {session.id === activeSessionId && (
                            <div className="absolute right-2 top-1/2 -translate-y-1/2">
                              <ChevronRight className="w-4 h-4 text-blue-400" />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>
        {`
          .neural-grid {
            background-image: 
              linear-gradient(to right, rgba(59, 130, 246, 0.1) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(147, 51, 234, 0.1) 1px, transparent 1px);
            background-size: 20px 20px;
            animation: flow 20s linear infinite;
          }

          @keyframes flow {
            0% {
              transform: translateY(0);
            }
            100% {
              transform: translateY(20px);
            }
          }

          .ease-out-expo {
            transition-timing-function: cubic-bezier(0.19, 1, 0.22, 1);
          }
        `}
      </style>
    </>
  );
}

export default ConversationSidebar;